import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

// Public pages
import Home from "@/pages/public/Home";
import About from "@/pages/public/About";
import Services from "@/pages/public/Services";
import Contact from "@/pages/public/Contact";
import Track from "@/pages/public/TrackShipment";
import Booking from "@/pages/public/Booking";

// Auth pages
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";

// Customer portal
import CustomerDashboard from "@/pages/customer/CustomerDashboard";
import MyShipments from "@/pages/customer/MyShipments";
import BookShipment from "@/pages/customer/BookShipment";
import Wallet from "@/pages/customer/Wallet";
import Profile from "@/pages/customer/Profile";

// Agent portal
import AgentDashboard from "@/pages/agent/AgentDashboard";
import ManageCustomers from "@/pages/agent/ManageCustomers";
import AgentBookShipment from "@/pages/agent/BookShipment";
import AgentMyBookings from "@/pages/agent/MyBookings";
import UpdateStatus from "@/pages/agent/UpdateStatus";

// Staff portal
import StaffDashboard from "@/pages/staff/StaffDashboard";
import BookingConsole from "@/pages/staff/BookingConsole";
import StaffMyOrders from "@/pages/staff/MyOrders";
import AssignmentConsole from "@/pages/staff/AssignmentConsole";
import PickupConsole from "@/pages/staff/PickupConsole";

// Partner portal
import PartnerDashboard from "@/pages/partner/PartnerDashboard";
import PartnerBookings from "@/pages/partner/PartnerBookings";
import PartnerShipments from "@/pages/partner/PartnerShipments";
import PartnerEarnings from "@/pages/partner/PartnerEarnings";
import ProofUpload from "@/pages/partner/ProofUpload";

// Admin panel
import AdminDashboard from "@/pages/admin/AdminDashboard";
import ManageUsers from "@/pages/admin/ManageUsers";
import AllShipments from "@/pages/admin/AllShipments";
import PricingConfig from "@/pages/admin/PricingConfig";
import Reports from "@/pages/admin/Reports";
import WarehouseManagement from "@/pages/admin/WarehouseManagement";
import OdaPincodeManagement from "@/pages/admin/OdaPincodeManagement";
import RateEngine from "@/pages/admin/RateEngine";
import WalletManagement from "@/pages/admin/WalletManagement";
import ReconciliationDashboard from "@/pages/admin/ReconciliationDashboard";
import WalletLedger from "@/pages/admin/WalletLedger";
import RateCalculator from "@/pages/RateCalculator";
import CustomerCreation from "@/pages/admin/CustomerCreation";
import RateAgreements from "@/pages/admin/RateAgreements";
import WalletLedgerV2 from "@/pages/admin/WalletLedgerV2";
import ManagePartners from "@/pages/admin/ManagePartners";
import ShipmentLedger from "@/pages/admin/ShipmentLedger";
import ReconciliationV2 from "@/pages/admin/ReconciliationV2";
import CustomerProfile from "@/pages/admin/CustomerProfile";
import CreatePartner from "@/pages/admin/CreatePartner";
import AdminInvoices from "@/pages/admin/Invoices";
import InvoiceDetail from "@/pages/admin/InvoiceDetail";
import AdminWalletDashboard from "@/pages/admin/WalletDashboard";
import AdminWalletTopup from "@/pages/admin/WalletTopup";
import AdminConfig from "@/pages/admin/AdminConfig";
import PickupLocations from "@/pages/admin/PickupLocations";
import CustomerInvoices from "@/pages/customer/Invoices";
import CustomerWalletTopup from "@/pages/customer/WalletTopup";
import ShipmentTracking from "@/pages/admin/ShipmentTracking";
import BulkUploadAdmin from "@/pages/admin/BulkUpload";
import BulkUploadStaff from "@/pages/staff/BulkUpload";
import PickupRequests from "@/pages/customer/PickupRequests";

// Phase 4 — additive (NDR, Disputes, Support, Demurrage, Insurance)
import NdrConsoleAdmin from "@/pages/admin/NdrConsole";
import NdrConsoleStaff from "@/pages/staff/NdrConsole";
import DisputesInbox from "@/pages/admin/DisputesInbox";
import CustomerDisputes from "@/pages/customer/Disputes";
import SupportTicketsAdmin from "@/pages/admin/SupportTickets";
import SupportTicketsStaff from "@/pages/staff/SupportTickets";
import SupportTicketsCustomer from "@/pages/customer/SupportTickets";
import DemurrageQueue from "@/pages/admin/DemurrageQueue";
import InsuranceClaimsAdmin from "@/pages/admin/InsuranceClaims";
import CustomerInsuranceClaims from "@/pages/customer/InsuranceClaims";
import OpsOverview from "@/pages/admin/OpsOverview";
import CustomerKYC from "@/pages/admin/CustomerKYC";

// Protected route component
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!user) return <Navigate to="/auth/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    if (user.role === "agent") return <Navigate to="/agent" replace />;
    return <Navigate to="/customer" replace />;
  }
  return children;
}

// Send authenticated users to their portal instead of the public landing page
function NotFoundRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    if (user.role === "agent") return <Navigate to="/agent" replace />;
    if (user.role === "staff") return <Navigate to="/staff" replace />;
    if (user.role === "partner") return <Navigate to="/partner" replace />;
    return <Navigate to="/customer" replace />;
  }
  return <Navigate to="/" replace />;
}

// Redirect to portal after login
function AuthRedirect({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    if (user.role === "agent") return <Navigate to="/agent" replace />;
    if (user.role === "staff") return <Navigate to="/staff" replace />;
    if (user.role === "partner") return <Navigate to="/partner" replace />;
    return <Navigate to="/customer" replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Home />} />
      <Route path="/about" element={<About />} />
      <Route path="/services" element={<Services />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/track" element={<Track />} />
      <Route path="/book" element={<Booking />} />

      {/* Auth routes */}
      <Route path="/auth/login" element={<AuthRedirect><Login /></AuthRedirect>} />
      <Route path="/auth/register" element={<AuthRedirect><Register /></AuthRedirect>} />

      {/* Customer Portal */}
      <Route path="/customer" element={<ProtectedRoute allowedRoles={["customer"]}><CustomerDashboard /></ProtectedRoute>} />
      <Route path="/customer/shipments" element={<ProtectedRoute allowedRoles={["customer"]}><MyShipments /></ProtectedRoute>} />
      <Route path="/customer/book" element={<ProtectedRoute allowedRoles={["customer"]}><BookShipment /></ProtectedRoute>} />
      <Route path="/customer/wallet" element={<ProtectedRoute allowedRoles={["customer"]}><Wallet /></ProtectedRoute>} />
      <Route path="/customer/profile" element={<ProtectedRoute allowedRoles={["customer"]}><Profile /></ProtectedRoute>} />
      <Route path="/customer/rate-calculator" element={<ProtectedRoute allowedRoles={["customer"]}><RateCalculator /></ProtectedRoute>} />

      {/* Agent Portal */}
      <Route path="/agent" element={<ProtectedRoute allowedRoles={["agent"]}><AgentDashboard /></ProtectedRoute>} />
      <Route path="/agent/customers" element={<ProtectedRoute allowedRoles={["agent"]}><ManageCustomers /></ProtectedRoute>} />
      <Route path="/agent/book" element={<ProtectedRoute allowedRoles={["agent"]}><AgentBookShipment /></ProtectedRoute>} />
      <Route path="/agent/bookings" element={<ProtectedRoute allowedRoles={["agent"]}><AgentMyBookings /></ProtectedRoute>} />
      <Route path="/agent/update-status" element={<ProtectedRoute allowedRoles={["agent"]}><UpdateStatus /></ProtectedRoute>} />

      {/* Staff Portal */}
      <Route path="/staff" element={<ProtectedRoute allowedRoles={["staff"]}><StaffDashboard /></ProtectedRoute>} />
      <Route path="/staff/book" element={<ProtectedRoute allowedRoles={["staff", "admin"]}><BookingConsole /></ProtectedRoute>} />
      <Route path="/staff/orders" element={<ProtectedRoute allowedRoles={["staff"]}><StaffMyOrders /></ProtectedRoute>} />
      <Route path="/staff/assignment" element={<ProtectedRoute allowedRoles={["staff", "admin"]}><AssignmentConsole /></ProtectedRoute>} />
      <Route path="/staff/customers" element={<ProtectedRoute allowedRoles={["staff"]}><ManageCustomers /></ProtectedRoute>} />
      <Route path="/staff/rate-calculator" element={<ProtectedRoute allowedRoles={["staff", "agent"]}><RateCalculator /></ProtectedRoute>} />

      {/* Partner Portal */}
      <Route path="/partner" element={<ProtectedRoute allowedRoles={["partner"]}><PartnerDashboard /></ProtectedRoute>} />
      <Route path="/partner/bookings" element={<ProtectedRoute allowedRoles={["partner"]}><PartnerBookings /></ProtectedRoute>} />
      <Route path="/partner/shipments" element={<ProtectedRoute allowedRoles={["partner"]}><PartnerShipments /></ProtectedRoute>} />
      <Route path="/partner/earnings" element={<ProtectedRoute allowedRoles={["partner"]}><PartnerEarnings /></ProtectedRoute>} />
      <Route path="/partner/upload-proof" element={<ProtectedRoute allowedRoles={["partner"]}><ProofUpload /></ProtectedRoute>} />

      {/* Admin Panel */}
      <Route path="/admin" element={<ProtectedRoute allowedRoles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/customers" element={<ProtectedRoute allowedRoles={["admin"]}><ManageUsers userRole="customer" /></ProtectedRoute>} />
      <Route path="/admin/customers/:id" element={<ProtectedRoute allowedRoles={["admin"]}><CustomerProfile /></ProtectedRoute>} />
      <Route path="/admin/agents" element={<ProtectedRoute allowedRoles={["admin"]}><ManageUsers userRole="agent" /></ProtectedRoute>} />
      <Route path="/admin/shipments" element={<ProtectedRoute allowedRoles={["admin"]}><AllShipments /></ProtectedRoute>} />
      <Route path="/admin/shipments/:id/tracking" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><ShipmentTracking /></ProtectedRoute>} />
      <Route path="/admin/bulk-upload" element={<ProtectedRoute allowedRoles={["admin"]}><BulkUploadAdmin /></ProtectedRoute>} />
      <Route path="/staff/bulk-upload" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><BulkUploadStaff /></ProtectedRoute>} />
      <Route path="/admin/warehouses" element={<ProtectedRoute allowedRoles={["admin"]}><WarehouseManagement /></ProtectedRoute>} />
      <Route path="/admin/oda-pincodes" element={<ProtectedRoute allowedRoles={["admin"]}><OdaPincodeManagement /></ProtectedRoute>} />
      <Route path="/admin/rate-engine" element={<ProtectedRoute allowedRoles={["admin"]}><RateEngine /></ProtectedRoute>} />
      <Route path="/admin/wallet-mgmt" element={<ProtectedRoute allowedRoles={["admin"]}><WalletManagement /></ProtectedRoute>} />
      <Route path="/admin/reconciliation" element={<ProtectedRoute allowedRoles={["admin"]}><ReconciliationDashboard /></ProtectedRoute>} />
      <Route path="/admin/wallet-ledger" element={<ProtectedRoute allowedRoles={["admin"]}><WalletLedger /></ProtectedRoute>} />
      <Route path="/admin/rate-calculator" element={<ProtectedRoute allowedRoles={["admin"]}><RateCalculator /></ProtectedRoute>} />
      <Route path="/admin/customer-mgmt" element={<ProtectedRoute allowedRoles={["admin"]}><CustomerCreation /></ProtectedRoute>} />
      <Route path="/admin/pricing" element={<ProtectedRoute allowedRoles={["admin"]}><PricingConfig /></ProtectedRoute>} />
      <Route path="/admin/reports" element={<ProtectedRoute allowedRoles={["admin"]}><Reports /></ProtectedRoute>} />
      <Route path="/admin/rate-agreements" element={<ProtectedRoute allowedRoles={["admin"]}><RateAgreements /></ProtectedRoute>} />
      <Route path="/admin/wallet-ledger-v2" element={<ProtectedRoute allowedRoles={["admin"]}><WalletLedgerV2 /></ProtectedRoute>} />
      <Route path="/admin/partners" element={<ProtectedRoute allowedRoles={["admin"]}><ManagePartners /></ProtectedRoute>} />
      <Route path="/admin/partners/new" element={<ProtectedRoute allowedRoles={["admin"]}><CreatePartner /></ProtectedRoute>} />
      <Route path="/admin/shipment-ledger" element={<ProtectedRoute allowedRoles={["admin"]}><ShipmentLedger /></ProtectedRoute>} />
      <Route path="/admin/reconciliation-v2" element={<ProtectedRoute allowedRoles={["admin"]}><ReconciliationV2 /></ProtectedRoute>} />
      <Route path="/admin/book" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><BookingConsole /></ProtectedRoute>} />
      <Route path="/admin/assignment" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><AssignmentConsole /></ProtectedRoute>} />
      <Route path="/admin/invoices" element={<ProtectedRoute allowedRoles={["admin"]}><AdminInvoices /></ProtectedRoute>} />
      <Route path="/admin/invoices/:id" element={<ProtectedRoute allowedRoles={["admin"]}><InvoiceDetail /></ProtectedRoute>} />
      <Route path="/admin/wallet-dashboard" element={<ProtectedRoute allowedRoles={["admin"]}><AdminWalletDashboard /></ProtectedRoute>} />
      <Route path="/admin/wallet/topup" element={<ProtectedRoute allowedRoles={["admin"]}><AdminWalletTopup /></ProtectedRoute>} />
      <Route path="/admin/config" element={<ProtectedRoute allowedRoles={["admin"]}><AdminConfig /></ProtectedRoute>} />
      <Route path="/admin/pickup-locations" element={<ProtectedRoute allowedRoles={["admin"]}><PickupLocations /></ProtectedRoute>} />
      <Route path="/admin/pickup-requests" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><PickupRequests /></ProtectedRoute>} />
      <Route path="/admin/dashboard" element={<Navigate to="/admin" replace />} />
      <Route path="/admin/wallet" element={<Navigate to="/admin/wallet-dashboard" replace />} />
      <Route path="/admin/insights" element={<Navigate to="/admin/ops-overview" replace />} />
      <Route path="/customer/invoices" element={<ProtectedRoute allowedRoles={["customer"]}><CustomerInvoices /></ProtectedRoute>} />
      <Route path="/customer/wallet/topup" element={<ProtectedRoute allowedRoles={["customer"]}><CustomerWalletTopup /></ProtectedRoute>} />
      <Route path="/customer/pickup-requests" element={<ProtectedRoute allowedRoles={["customer", "staff", "admin"]}><PickupRequests /></ProtectedRoute>} />
      <Route path="/staff/pickup-console" element={<ProtectedRoute allowedRoles={["staff", "admin"]}><PickupConsole /></ProtectedRoute>} />

      {/* Phase 4 — NDR / Disputes / Support / Demurrage / Insurance */}
      <Route path="/admin/ndr" element={<ProtectedRoute allowedRoles={["admin"]}><NdrConsoleAdmin /></ProtectedRoute>} />
      <Route path="/staff/ndr" element={<ProtectedRoute allowedRoles={["staff", "admin"]}><NdrConsoleStaff /></ProtectedRoute>} />
      <Route path="/admin/disputes" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><DisputesInbox /></ProtectedRoute>} />
      <Route path="/admin/disputes/:id" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><DisputesInbox /></ProtectedRoute>} />
      <Route path="/customer/disputes" element={<ProtectedRoute allowedRoles={["customer"]}><CustomerDisputes /></ProtectedRoute>} />
      <Route path="/admin/support" element={<ProtectedRoute allowedRoles={["admin"]}><SupportTicketsAdmin /></ProtectedRoute>} />
      <Route path="/admin/support/:id" element={<ProtectedRoute allowedRoles={["admin"]}><SupportTicketsAdmin /></ProtectedRoute>} />
      <Route path="/staff/support" element={<ProtectedRoute allowedRoles={["staff", "admin"]}><SupportTicketsStaff /></ProtectedRoute>} />
      <Route path="/staff/support/:id" element={<ProtectedRoute allowedRoles={["staff", "admin"]}><SupportTicketsStaff /></ProtectedRoute>} />
      <Route path="/customer/support" element={<ProtectedRoute allowedRoles={["customer"]}><SupportTicketsCustomer /></ProtectedRoute>} />
      <Route path="/customer/support/:id" element={<ProtectedRoute allowedRoles={["customer"]}><SupportTicketsCustomer /></ProtectedRoute>} />
      <Route path="/admin/demurrage" element={<ProtectedRoute allowedRoles={["admin"]}><DemurrageQueue /></ProtectedRoute>} />
      <Route path="/admin/insurance-claims" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><InsuranceClaimsAdmin /></ProtectedRoute>} />
      <Route path="/customer/insurance-claims" element={<ProtectedRoute allowedRoles={["customer"]}><CustomerInsuranceClaims /></ProtectedRoute>} />
      <Route path="/admin/ops-overview" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><OpsOverview /></ProtectedRoute>} />
      <Route path="/admin/customers/new" element={<ProtectedRoute allowedRoles={["admin"]}><CustomerKYC /></ProtectedRoute>} />

      {/* Fallback */}
      <Route path="*" element={<NotFoundRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
